import { isDefined, toError } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import type { Worker } from "@src/appV2/Worker/api/types";
import { useEffect, useRef } from "react";

import { useUpdateOnboardingWorker } from "../api/useUpdateOnboardingWorker";
import { ONBOARDING_STAGES, type OnboardingStage } from "../util/constants";
import { logOnboardingError } from "../util/segment";

interface Props {
  worker: Worker | undefined;
  stage: OnboardingStage;
}

export function useSaveOnboardingStageEffect(props: Props) {
  const { stage, worker } = props;
  const workerId = worker?.userId;

  const { mutateAsync: updateOnboardingWorker } = useUpdateOnboardingWorker();

  // Defensive check to prevent calling mutation if already called, to avoid useEffect shenanigans
  const isStageUpdated = useRef(false);

  /**
   * When user starts on email step, we don't have Worker object yet, so we don't have workerId
   * But we want to log the step started event for email step even without workerId.
   * Because it will still be useful for aggregate analytics.
   *
   * But for other steps, we want to log the step started event only if we have workerId
   *
   */
  const canLogStepStartedEvent = isDefined(workerId) || stage === ONBOARDING_STAGES.EMAIL;

  useLogEffect(
    APP_V2_USER_EVENTS.ONBOARDING_STEP_STARTED,
    {
      stepStarted: stage,
      workerId,
    },
    {
      enabled: canLogStepStartedEvent,
    }
  );

  useEffect(() => {
    if (!isStageUpdated.current && workerId) {
      void updateOnboardingWorker({
        stage: "updateStageOnly",
        newStage: stage,
        signupFlowVersion: "V2",
      })
        .then(() => {
          isStageUpdated.current = true;
        })
        .catch((error: unknown) => {
          logOnboardingError(stage, toError(error), {
            eventMeta: {
              newStage: stage,
            },
          });
        });
    }
  }, [stage, updateOnboardingWorker, workerId]);
}
